import {captureException} from '@zzng/common/utils/matrix';

import HeadlineATemplate from '@/root/components/CardTemplate/templates/HeadlineATemplate';
import HeadlineBTemplate from '@/root/components/CardTemplate/templates/HeadlineBTemplate';
import HeadlineCTemplate from '@/root/components/CardTemplate/templates/HeadlineCTemplate';
import HeadlineDoorkeyTemplate from '@/root/components/CardTemplate/templates/HeadlineDoorkeyTemplate';
import ImageTemplate from '@/root/components/CardTemplate/templates/ImageTemplate';
import ProfileATemplate from '@/root/components/CardTemplate/templates/ProfileATemplate';
import ProfileBadgeTemplate from '@/root/components/CardTemplate/templates/ProfileBadgeTemplate';
import ProfileBTemplate from '@/root/components/CardTemplate/templates/ProfileBTemplate';
import ProfileWebpTemplate from '@/root/components/CardTemplate/templates/ProfileWebpTemplate';
import {
  BaseCardTemplateProps,
  Card,
  DesignTemplateCode,
  HeadlineDoorkeyTemplateProps,
  HeadlineTemplateProps,
  ImageTemplateProps,
  ProfileBadgeTemplateProps,
  ProfileTemplatePropsWithoutSubTitle,
  ProfileTemplatePropsWithSubTitle,
  TEMPLATE_KEYS
} from '@/root/models/card';

const REQUIRED_TEMPLATE_KEYS: Record<
  DesignTemplateCode,
  | typeof TEMPLATE_KEYS.HEADLINE
  | typeof TEMPLATE_KEYS.PROFILE
  | typeof TEMPLATE_KEYS.PROFILE_WITH_SUBTITLE
  | typeof TEMPLATE_KEYS.PROFILE_BADGE
  | typeof TEMPLATE_KEYS.IMAGE
  | typeof TEMPLATE_KEYS.HEADLINE_DOORKEY
> = {
  [DesignTemplateCode.HEADLINE_A]: TEMPLATE_KEYS.HEADLINE,
  [DesignTemplateCode.HEADLINE_B]: TEMPLATE_KEYS.HEADLINE,
  [DesignTemplateCode.HEADLINE_C]: TEMPLATE_KEYS.HEADLINE,
  [DesignTemplateCode.PROFILE_A]: TEMPLATE_KEYS.PROFILE,
  [DesignTemplateCode.PROFILE_B]: TEMPLATE_KEYS.PROFILE_WITH_SUBTITLE,
  [DesignTemplateCode.PROFILE_WEBP]: TEMPLATE_KEYS.PROFILE_WITH_SUBTITLE,
  [DesignTemplateCode.PROFILE_BADGE]: TEMPLATE_KEYS.PROFILE_BADGE,
  [DesignTemplateCode.IMAGE]: TEMPLATE_KEYS.IMAGE,
  [DesignTemplateCode.HEADLINE_DOORKEY]: TEMPLATE_KEYS.HEADLINE_DOORKEY
};

interface Props extends BaseCardTemplateProps {
  card: Card;
}

const CardTemplate = ({card, ...rest}: Props) => {
  const {designTemplateCode, content} = card;
  const props = {...content, ...rest};

  const requiredKeys = REQUIRED_TEMPLATE_KEYS[designTemplateCode];
  const missingKeys = requiredKeys.filter((key) => !props[key]);

  if (missingKeys.length > 0) {
    captureException(`${designTemplateCode} 에서 필요한 키가 누락되었습니다: ${missingKeys.join(', ')}`);
  }

  switch (designTemplateCode) {
    case DesignTemplateCode.HEADLINE_A:
      return <HeadlineATemplate {...(props as HeadlineTemplateProps)} />;
    case DesignTemplateCode.HEADLINE_B:
      return <HeadlineBTemplate {...(props as HeadlineTemplateProps)} />;
    case DesignTemplateCode.HEADLINE_C:
      return <HeadlineCTemplate {...(props as HeadlineTemplateProps)} />;
    case DesignTemplateCode.PROFILE_A:
      return <ProfileATemplate {...(props as ProfileTemplatePropsWithoutSubTitle)} />;
    case DesignTemplateCode.PROFILE_B:
      return <ProfileBTemplate {...(props as ProfileTemplatePropsWithSubTitle)} />;
    case DesignTemplateCode.PROFILE_WEBP:
      return <ProfileWebpTemplate {...(props as ProfileTemplatePropsWithSubTitle)} />;
    case DesignTemplateCode.PROFILE_BADGE:
      return <ProfileBadgeTemplate {...(props as ProfileBadgeTemplateProps)} />;
    case DesignTemplateCode.IMAGE:
      return <ImageTemplate {...(props as ImageTemplateProps)} alt={card.overview.title} />;
    case DesignTemplateCode.HEADLINE_DOORKEY:
      return <HeadlineDoorkeyTemplate {...(props as HeadlineDoorkeyTemplateProps)} />;
    default:
      throw new Error(`designTemplateCode is invalid: ${card.designTemplateCode}`);
  }
};

export default CardTemplate;
